import { CtaType } from "@/components/molecules/CtaButtonGroup";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import SectionHeader from "./shared/SectionHeader";

/**
 * VideoShortsSection — header + grid of vertical (9:16) YouTube Shorts,
 * each with a title and one-line description underneath.
 *
 * Cards render the YouTube thumbnail and only swap in the iframe on click,
 * so the page doesn't pay for four players on load.
 */

export type VideoShortItem = {
  /** YouTube video id, e.g. "tPiNw6-eoyo" from youtube.com/shorts/tPiNw6-eoyo */
  youtubeId: string;
  title: string;
  description?: string;
};

export type VideoShortsSectionData = {
  eyebrow?: string;
  titleMuted?: string;
  titleBright?: string;
  title?: string;
  subtitle?: string;
  ctas?: CtaType[];
  /** Small stat strip rendered between the header and the videos. */
  stats?: Array<{ value: string; label: string }>;
  videos: VideoShortItem[];
};

const VideoCard = ({ video }: { video: VideoShortItem }) => {
  const [playing, setPlaying] = useState(false);
  // Shorts usually have a vertical "oardefault" thumbnail; not every video
  // does, so fall back to the landscape one (cropped by object-cover).
  const [thumb, setThumb] = useState<"oardefault" | "hqdefault">("oardefault");
  const showLandscapeThumb = () => setThumb("hqdefault");
  const imgRef = useRef<HTMLImageElement>(null);
  // A missing oardefault comes back as a 120x90 placeholder (or an error).
  const checkThumb = (img: HTMLImageElement) => {
    if (img.naturalWidth <= 120) showLandscapeThumb();
  };
  // The SSR'd <img> can finish (or fail) before hydration attaches
  // onLoad/onError, so re-check it once on mount.
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth <= 120) setThumb("hqdefault");
  }, []);
  return (
    <div className="flex flex-col">
      <div className="relative aspect-[9/16] w-full overflow-hidden rounded-3xl bg-neutralBlack shadow-[0_8px_24px_rgba(20,20,40,0.08)]">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0&playsinline=1`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play video: ${video.title}`}
            className="group absolute inset-0 h-full w-full"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={`https://i.ytimg.com/vi/${video.youtubeId}/${thumb}.jpg`}
              alt=""
              loading="lazy"
              ref={imgRef}
              onError={showLandscapeThumb}
              onLoad={(e) => checkThumb(e.currentTarget)}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/30" />
            <span className="absolute left-1/2 top-1/2 inline-flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform group-hover:scale-105">
              <FontAwesomeIcon
                icon={faPlay}
                className="ml-0.5 h-5 w-5 text-neutralBlack"
                aria-hidden="true"
              />
            </span>
          </button>
        )}
      </div>
      <h3 className="mt-4 font-inter text-[16px] font-semibold leading-snug text-neutralBlack md:text-[17px]">
        {video.title}
      </h3>
      {video.description ? (
        <p className="mt-2 font-inter text-[14px] leading-relaxed text-darkSlate04">
          {video.description}
        </p>
      ) : null}
    </div>
  );
};

const VideoShortsSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  title,
  subtitle,
  ctas,
  stats,
  videos,
}: VideoShortsSectionData) => (
  <section className="relative w-full bg-white">
    <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
      <SectionHeader
        eyebrow={eyebrow}
        titleMuted={titleMuted}
        titleBright={titleBright}
        title={title}
        subtitle={subtitle}
        ctas={ctas}
        variant="light-teal"
        align="center"
      />
      {stats && stats.length ? (
        <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3 md:mt-12">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-neutralBlack/10 bg-white px-5 py-4 text-center"
            >
              <div className="font-inter text-[28px] font-semibold text-neutralBlack md:text-[32px]">
                {s.value}
              </div>
              <div className="mt-1 font-inter text-[14px] text-darkSlate04">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="mx-auto mt-12 grid max-w-6xl grid-cols-2 gap-x-4 gap-y-8 md:mt-16 lg:grid-cols-4 lg:gap-6">
        {videos.map((v) => (
          <VideoCard key={v.youtubeId} video={v} />
        ))}
      </div>
    </div>
  </section>
);

export default VideoShortsSection;
